import React, { FC, useEffect, useContext } from 'react';
import { ContextApi } from 'react-uforms';
import ReactMarkdown from 'react-markdown';
import { Question, questionFieldType, QuestionRequired } from '@ch/common/types/data';
import { FormLabel } from '@ch/common/components/form/label';
import { Heading } from '@ch/common/components/heading';
import { Box } from '@ch/common/components/box';
import { Text } from '@ch/common/components/text';
import { Content } from '@ch/common/components/content';
import { FormError } from '@ch/common/components/form/error';
import QuizFieldInner from './quiz-field-inner';
import QuizTooltip from '@ch/common/components/quiz-tooltip/quiz-tooltip';
import QuizFieldHints from './quiz-field-hints';
import { useData } from '../use-data';
import { QuizPersonaWrapper } from './quiz.styled';
import { Col, Container, Row } from 'styled-bootstrap-grid';

const QuizField: FC<Question & { fullWidth?: boolean }> = props => {
  const { setValue, setErrors } = useContext(ContextApi);
  const {
    identifier: id,
    question,
    field_type: type,
    sub_question: subQuestion,
    required,
    hidden,
    value,
    children,
    tooltip,
    field_hints: hints = [],
    fullWidth = false,
    isPrivacy = false,
  } = props;
  const {
    data: {
      keywords: { choosePersona },
    },
  } = useData();

  //HACK: conditional default values
  useEffect(() => {
    setValue(id, value);
  }, [id, value, setValue]);

  //HACK: take validation errors away since the field is not required
  useEffect(() => {
    if (required !== QuestionRequired.YES) {
      setErrors(id, []);
    }
  }, [required, id, setErrors]);

  const label = fullWidth ? (
    <Heading as="h2" $size="h2" $align="center" $m={[0, 0, 25]} $color="primary">
      {question}
    </Heading>
  ) : (
    <FormLabel m={[0]} measure="md">
      <span dangerouslySetInnerHTML={{ __html: question }} />
      {required === QuestionRequired.YES && !isPrivacy && (
        <Text as="span" $m={[0, 0, 0, 3]} $color="error" $size="lg">
          *
        </Text>
      )}
      {tooltip && <QuizTooltip {...{ id }}>{tooltip}</QuizTooltip>}
    </FormLabel>
  );

  const noHintsFiled = [questionFieldType.DOC_UPLOAD];
  const noSubLabelField = [
    questionFieldType.DIALOG_BOX,
    questionFieldType.MANUAL_DIALOG_BOX,
    questionFieldType.NOTIFICATION,
    questionFieldType.DOC_UPLOAD,
    questionFieldType.PERSON_TYPE,
  ];
  const noLabelField = [...noSubLabelField, questionFieldType.DUPLICATOR];
  const scrollDataRequiredTypes = [questionFieldType.DIALOG_BOX, questionFieldType.DOC_UPLOAD];
  const scrollData = scrollDataRequiredTypes.includes(type) ? `${props.identifier}_required` : props.identifier;

  return !hidden ? (
    <>
      <Box $mb={25} data={scrollData}>
        {!noLabelField.includes(type) && <>{question && label}</>}
        {!noSubLabelField.includes(type) && subQuestion && (
          <Content {...{ $size: 'sm', $color: 'primary', $m: [4, 0, 0] }}>
            <ReactMarkdown>{subQuestion}</ReactMarkdown>
          </Content>
        )}
        {questionFieldType.PERSON_TYPE === type ? (
          <QuizPersonaWrapper>
            <Container>
              <Row justifyContent="center">
                <Col xs={12} sm={10}>
                  <Heading $align="center" $size="h3" $fontFamily="aeonikBold" $m={[0, 0, 15, 0]}>
                    {choosePersona}
                  </Heading>

                  <Box>
                    <QuizFieldInner {...props} />
                  </Box>
                </Col>
              </Row>
            </Container>
          </QuizPersonaWrapper>
        ) : (
          <Box $m={[8, 0, 0]}>
            <QuizFieldInner {...props} />
          </Box>
        )}
        {!fullWidth && !children && <FormError name={id} />}
        {!noHintsFiled.includes(type) && <QuizFieldHints hints={hints} />}
      </Box>
    </>
  ) : null;
};

export default QuizField;
